/**
 * v3.18-1256 PAYMENT-002 — Payment Center page (transaction foundation).
 *
 * Provider-neutral. Processor name appears nowhere. Live provider data
 * only — no caching, no polling, no snapshots.
 *
 * Sections: Overview • Payment Requests • Payment History • Customers
 * • Products • POS Links (pending upstream docs).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, Ban, PlugZap, Activity,
  Plus, Trash2, Search, ExternalLink, AlertTriangle, FileText, Users, Package,
  ScrollText, Link as LinkIcon, LayoutGrid, Webhook, KeyRound, Copy, Zap, User,
} from 'lucide-react';
import ChargeCardDialog from '../components/ChargeCardDialog';
import ReceiptViewer from '../components/ReceiptViewer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';

// ───────────────────────────────────────────────────────────
// Shared helpers
// ───────────────────────────────────────────────────────────
const STATUS_META = {
  online:               { label: 'Online',               dot: 'bg-emerald-500', text: 'text-emerald-700', ring: 'ring-emerald-500/30', icon: ShieldCheck },
  offline:              { label: 'Offline',              dot: 'bg-red-500',     text: 'text-red-700',     ring: 'ring-red-500/30',     icon: ShieldAlert },
  verifying:            { label: 'Verifying',            dot: 'bg-blue-500',    text: 'text-blue-700',    ring: 'ring-blue-500/30',    icon: Loader2 },
  disabled:             { label: 'Disabled',             dot: 'bg-slate-400',   text: 'text-slate-500',   ring: 'ring-slate-300/40',   icon: Ban },
  configuration_error:  { label: 'Configuration Error',  dot: 'bg-amber-500',   text: 'text-amber-700',   ring: 'ring-amber-500/30',   icon: PlugZap },
  authentication_failed:{ label: 'Authentication Failed',dot: 'bg-red-600',     text: 'text-red-800',     ring: 'ring-red-600/30',     icon: ShieldAlert },
  not_configured:       { label: 'Not Configured',       dot: 'bg-amber-500',   text: 'text-amber-700',   ring: 'ring-amber-500/30',   icon: PlugZap },
};

const fmtTime = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtMoney = (amt, ccy) => {
  if (amt == null || amt === '') return '—';
  const n = typeof amt === 'string' ? parseFloat(amt) : amt;
  if (Number.isNaN(n)) return String(amt);
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: (ccy || 'USD').toUpperCase() }).format(n); }
  catch { return `${(ccy || 'USD').toUpperCase()} ${n.toFixed(2)}`; }
};

// Render a normalized envelope error (from /api/payment-center/*).
const EnvelopeError = ({ envelope }) => {
  if (!envelope || envelope.ok !== false) return null;
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2" data-testid="payment-center-envelope-error">
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-medium">Provider request failed{envelope.error_code ? ` (${envelope.error_code})` : ''}</p>
        {envelope.error_message ? <p className="text-xs mt-0.5 text-red-700/90">{envelope.error_message}</p> : null}
      </div>
    </div>
  );
};

// A common panel wrapper with loading / error / empty states.
const Section = ({ title, description, actions, loading, envelope, isEmpty, emptyMessage, children, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-start justify-between gap-6 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description ? <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
    <EnvelopeError envelope={envelope} />
    {loading ? (
      <div className="flex items-center gap-2 text-slate-500 py-8"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Loading from provider…</span></div>
    ) : envelope?.ok === false ? null : isEmpty ? (
      <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
        {emptyMessage || 'No records to display.'}
      </div>
    ) : children}
  </div>
);

// ───────────────────────────────────────────────────────────
// Terminals section (unchanged from v3.18-1255)
// ───────────────────────────────────────────────────────────
const TerminalCard = ({ terminal, onVerify, verifying }) => {
  const meta = STATUS_META[terminal.status] || STATUS_META.offline;
  const Icon = meta.icon;
  const disabled = !terminal.enabled || terminal.status === 'disabled';
  const isConfigError = terminal.status === 'configuration_error';
  const isAuthFailed = terminal.status === 'authentication_failed';
  const hasProblem = isConfigError || isAuthFailed;
  const hintText = disabled
    ? 'Terminal is not enabled. Contact an administrator to configure.'
    : isConfigError
    ? 'Terminal is enabled but required credentials are missing. Set the provider security key in backend/.env.'
    : isAuthFailed
    ? 'The provider rejected the current credentials. Regenerate/rotate the security key and re-verify.'
    : 'Live provider connectivity check.';
  return (
    <Card data-testid={`payment-center-terminal-card-${terminal.terminal_id}`} className={`relative overflow-hidden border ${disabled ? 'bg-slate-50 border-slate-200' : hasProblem ? 'bg-amber-50/40 border-amber-200' : 'bg-white border-slate-200'}`}>
      <div className={`absolute inset-x-0 top-0 h-1 ${meta.dot}`} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`text-lg font-semibold ${disabled ? 'text-slate-500' : 'text-slate-900'}`} data-testid={`payment-center-terminal-label-${terminal.terminal_id}`}>{terminal.label}</h3>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${terminal.role === 'primary' ? 'border-emerald-300 text-emerald-700' : 'border-slate-300 text-slate-500'}`}>{terminal.role === 'primary' ? 'Primary' : 'Secondary'}</Badge>
            </div>
            <p className="mt-1 text-xs text-slate-500">{hintText}</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${meta.ring} ${meta.text} bg-white`}>
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${meta.dot}`} />
            <span className="text-xs font-semibold" data-testid={`payment-center-terminal-status-${terminal.terminal_id}`}>{meta.label}</span>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div className="text-slate-500">Last check</div>
          <div className="text-slate-700 font-medium">{fmtTime(terminal.checked_at)}</div>
          <div className="text-slate-500">Latency</div>
          <div className="text-slate-700 font-medium">{terminal.latency_ms != null ? `${terminal.latency_ms} ms` : '—'}</div>
          {terminal.error_message ? (<><div className="text-slate-500">Detail</div><div className="text-red-700 font-medium">{terminal.error_message}</div></>) : null}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Icon className={`h-3.5 w-3.5 ${terminal.status === 'verifying' ? 'animate-spin' : ''}`} />
            <span>{disabled ? 'Verification unavailable' : terminal.status === 'online' ? 'Provider connection verified' : 'Verification required'}</span>
          </div>
          <Button size="sm" variant="outline" disabled={disabled || verifying} onClick={() => onVerify(terminal.terminal_id)} data-testid={`payment-center-terminal-verify-btn-${terminal.terminal_id}`}>
            {verifying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Verify Connection
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const OverviewSection = () => {
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const res = await api.get('/payment-center/terminals'); setTerminals(res.data?.terminals || []); }
    catch (e) { const m = e?.response?.data?.detail || 'Failed to load terminals'; setError(typeof m === 'string' ? m : 'Failed to load terminals'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const verify = async (id) => {
    setVerifyingId(id);
    try {
      const res = await api.post(`/payment-center/terminals/${id}/verify`);
      setTerminals(prev => prev.map(t => t.terminal_id === id ? { ...t, ...res.data } : t));
      const u = res.data;
      if (u.status === 'online') toast.success(`${u.label}: connection verified`);
      else if (u.status === 'disabled') toast.info(`${u.label}: terminal disabled`);
      else toast.warning(`${u.label}: ${u.error_message || 'connection failed'}`);
    } catch (e) { const m = e?.response?.data?.detail || 'Verification failed'; toast.error(typeof m === 'string' ? m : 'Verification failed'); }
    finally { setVerifyingId(null); }
  };
  return (
    <div data-testid="payment-center-overview-section">
      <div className="flex items-start justify-between gap-6 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Terminals</h2>
          <p className="text-xs text-slate-500 mt-0.5">Live status of the Hub&apos;s provisioned payment terminals. Every check queries the provider in real time — no cached values, no snapshots.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="payment-center-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Activity className="h-4 w-4 mr-1.5" />}Refresh
        </Button>
      </div>
      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="payment-center-error">{error}</div> : null}
      {loading && terminals.length === 0 ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Checking terminals…</span></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payment-center-terminal-grid">
          {terminals.map(t => <TerminalCard key={t.terminal_id} terminal={t} onVerify={verify} verifying={verifyingId === t.terminal_id} />)}
        </div>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────────────────
// Reusable list section builder
// ───────────────────────────────────────────────────────────
const useListEndpoint = (path, extraParams = {}) => {
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [keywords, setKeywords] = useState('');
  const stableExtra = useMemo(() => extraParams, [JSON.stringify(extraParams)]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, ...(keywords ? { keywords } : {}), ...stableExtra };
      const res = await api.get(path, { params });
      setEnvelope(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Request failed';
      setEnvelope({ ok: false, error_code: 'hub_error', error_message: typeof msg === 'string' ? msg : 'Request failed', data: null });
    } finally { setLoading(false); }
  }, [path, page, limit, keywords, stableExtra]);
  useEffect(() => { load(); }, [load]);
  return { envelope, loading, page, setPage, limit, keywords, setKeywords, reload: load };
};

// ───────────────────────────────────────────────────────────
// Payment Requests section (list + create dialog)
// ───────────────────────────────────────────────────────────
const PaymentRequestCreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_first_name: '', customer_last_name: '', email: '',
    amount: '', currency: 'USD', reference_number: '', request_methods: 'EMAIL',
  });
  const submit = async () => {
    if (!form.customer_first_name.trim()) { toast.error('First name is required'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Amount must be greater than 0'); return; }
    setSaving(true);
    try {
      const body = {
        request_methods: [form.request_methods],
        customer_first_name: form.customer_first_name.trim(),
        customer_last_name: form.customer_last_name?.trim() || undefined,
        email: form.email?.trim() || undefined,
        amount: parseFloat(form.amount),
        currency: (form.currency || 'USD').toUpperCase(),
        fixed_amount: true, payment_type: 'DB',
        reference_number: form.reference_number?.trim() || undefined,
      };
      const res = await api.post('/payment-center/payment-requests', body);
      if (res.data?.ok) {
        toast.success(`Payment request created — ${res.data.data?.transaction_id || 'success'}`);
        onCreated?.();
        onOpenChange(false);
        setForm({ customer_first_name: '', customer_last_name: '', email: '', amount: '', currency: 'USD', reference_number: '', request_methods: 'EMAIL' });
      } else {
        toast.error(res.data?.error_message || `Provider rejected the request (${res.data?.error_code || 'error'})`);
      }
    } catch (e) {
      const m = e?.response?.data?.detail || 'Request failed';
      toast.error(typeof m === 'string' ? m : 'Request failed');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payment-center-pr-create-dialog">
        <DialogHeader><DialogTitle>New Payment Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer first name *</Label><Input value={form.customer_first_name} onChange={e => setForm({ ...form, customer_first_name: e.target.value })} data-testid="pr-first-name" /></div>
            <div><Label>Customer last name</Label><Input value={form.customer_last_name} onChange={e => setForm({ ...form, customer_last_name: e.target.value })} /></div>
          </div>
          <div><Label>Customer email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Amount *</Label><Input type="number" step="0.01" min="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="pr-amount" /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} /></div>
          </div>
          <div><Label>Reference number</Label><Input value={form.reference_number} onChange={e => setForm({ ...form, reference_number: e.target.value })} /></div>
          <div><Label>Delivery method</Label>
            <select className="w-full border rounded-md h-9 px-3 text-sm" value={form.request_methods} onChange={e => setForm({ ...form, request_methods: e.target.value })}>
              <option value="EMAIL">Email invoice</option>
              <option value="SMS">SMS invoice</option>
              <option value="WEB">Web only (no invoice)</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="pr-submit-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Create request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaymentRequestsSection = () => {
  const { envelope, loading, page, setPage, keywords, setKeywords, reload } = useListEndpoint('/payment-center/payment-requests');
  const [createOpen, setCreateOpen] = useState(false);
  const rows = envelope?.ok ? (envelope.data?.transactions || []) : [];
  const totalPages = envelope?.ok ? parseInt(envelope.data?.page_count || 1, 10) : 1;
  const del = async (tx) => {
    if (!window.confirm(`Delete payment request ${tx}? This is only allowed if it has no payment history.`)) return;
    try {
      const res = await api.delete(`/payment-center/payment-requests/${tx}`);
      if (res.data?.ok) { toast.success(`Payment request ${tx} deleted`); reload(); }
      else toast.error(res.data?.error_message || 'Delete failed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <Section
      title="Payment Requests"
      description="One-time payment invoices sent to customers by email, SMS, or web link. Data is fetched live on every load."
      testId="payment-center-payment-requests-section"
      loading={loading}
      envelope={envelope}
      isEmpty={envelope?.ok && rows.length === 0}
      emptyMessage="No payment requests found for this page."
      actions={
        <>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
            <Input className="pl-8 h-9 w-56" placeholder="Search (reference / email)" value={keywords} onChange={e => setKeywords(e.target.value)} data-testid="pr-search" />
          </div>
          <Button size="sm" variant="outline" onClick={reload} disabled={loading}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="pr-new-btn"><Plus className="h-4 w-4 mr-1.5" />New request</Button>
        </>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Transaction</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Created</th>
              <th className="text-left px-3 py-2">Link</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.transaction_id} className="border-t border-slate-100" data-testid={`pr-row-${r.transaction_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.transaction_id}</td>
                <td className="px-3 py-2">{r.reference_number || '—'}</td>
                <td className="px-3 py-2">{r.email || r.mobile_number || '—'}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtMoney(r.amount, r.currency)}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.status || '—'}</Badge></td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtTime(r.created_at)}</td>
                <td className="px-3 py-2">{r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 inline-flex items-center gap-1"><LinkIcon className="h-3 w-3" />Open</a> : '—'}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => del(r.transaction_id)} data-testid={`pr-delete-${r.transaction_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span>Page {page} of {Math.max(totalPages, 1)}{envelope?.ok && envelope.data?.total_count != null ? ` — ${envelope.data.total_count} total` : ''}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
      <PaymentRequestCreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={reload} />
    </Section>
  );
};

// ───────────────────────────────────────────────────────────
// Payment History section (list only — detail URL undocumented)
// ───────────────────────────────────────────────────────────
const PaymentHistorySection = () => {
  const { envelope, loading, page, setPage, reload } = useListEndpoint('/payment-center/payment-history');
  const rows = envelope?.ok ? (envelope.data?.transactions || envelope.data?.payments || envelope.data?.data || []) : [];
  const totalPages = envelope?.ok ? parseInt(envelope.data?.page_count || 1, 10) : 1;
  return (
    <Section
      title="Payment History"
      description="Historic payment records from the provider. Read-only in this phase — refunds, captures, and detail views are deferred."
      testId="payment-center-payment-history-section"
      loading={loading} envelope={envelope}
      isEmpty={envelope?.ok && rows.length === 0}
      emptyMessage="No payment history entries returned for this page."
      actions={<Button size="sm" variant="outline" onClick={reload} disabled={loading}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>}
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Payment ID</th>
              <th className="text-left px-3 py-2">Transaction</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Payment date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.payment_id || r.transaction_id || i} className="border-t border-slate-100" data-testid={`ph-row-${r.payment_id || r.transaction_id || i}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.payment_id || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.transaction_id || '—'}</td>
                <td className="px-3 py-2">{r.reference_number || '—'}</td>
                <td className="px-3 py-2">{r.email || r.mobile_number || '—'}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtMoney(r.amount, r.currency)}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.status || '—'}</Badge></td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtTime(r.payment_date || r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span>Page {page} of {Math.max(totalPages, 1)}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </Section>
  );
};

// ───────────────────────────────────────────────────────────
// Customers section (list + create + delete)
// ───────────────────────────────────────────────────────────
const CustomerCreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', mobile_no: '', company: '', listing_type: 'individual' });
  const submit = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v && String(v).trim().length));
      const res = await api.post('/payment-center/customers', body);
      if (res.data?.ok) { toast.success('Customer created'); onCreated?.(); onOpenChange(false); setForm({ first_name: '', last_name: '', email: '', mobile_no: '', company: '', listing_type: 'individual' }); }
      else toast.error(res.data?.error_message || `Provider rejected (${res.data?.error_code || 'error'})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Request failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payment-center-cust-create-dialog">
        <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="cust-first-name" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input value={form.mobile_no} onChange={e => setForm({ ...form, mobile_no: e.target.value })} /></div>
          </div>
          <div><Label>Company</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
          <div><Label>Type</Label>
            <select className="w-full border rounded-md h-9 px-3 text-sm" value={form.listing_type} onChange={e => setForm({ ...form, listing_type: e.target.value })}>
              <option value="individual">Individual</option>
              <option value="company">Company</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cust-submit-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Create customer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CustomersSection = () => {
  const { envelope, loading, page, setPage, keywords, setKeywords, reload } = useListEndpoint('/payment-center/customers');
  const [createOpen, setCreateOpen] = useState(false);
  const rows = envelope?.ok ? (envelope.data?.customers || envelope.data?.data || []) : [];
  const totalPages = envelope?.ok ? parseInt(envelope.data?.page_count || 1, 10) : 1;
  const del = async (id) => {
    if (!window.confirm(`Delete customer ${id}?`)) return;
    try {
      const res = await api.delete(`/payment-center/customers/${id}`);
      if (res.data?.ok) { toast.success('Customer deleted'); reload(); }
      else toast.error(res.data?.error_message || 'Delete failed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <Section
      title="Customers"
      description="Customer directory pulled live from the provider. Create, view, and delete supported."
      testId="payment-center-customers-section"
      loading={loading} envelope={envelope}
      isEmpty={envelope?.ok && rows.length === 0}
      emptyMessage="No customers found."
      actions={
        <>
          <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" /><Input className="pl-8 h-9 w-56" placeholder="Search customers" value={keywords} onChange={e => setKeywords(e.target.value)} /></div>
          <Button size="sm" variant="outline" onClick={reload} disabled={loading}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="cust-new-btn"><Plus className="h-4 w-4 mr-1.5" />New customer</Button>
        </>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Mobile</th>
            <th className="text-left px-3 py-2">Company</th><th className="text-right px-3 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((c, i) => {
              const id = c.customer_id || c.id;
              const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || '—';
              return (
                <tr key={id || i} className="border-t border-slate-100" data-testid={`cust-row-${id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{id || '—'}</td>
                  <td className="px-3 py-2">{name}</td>
                  <td className="px-3 py-2">{c.email || '—'}</td>
                  <td className="px-3 py-2">{c.mobile_no || c.phone || '—'}</td>
                  <td className="px-3 py-2">{c.company || '—'}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => del(id)} disabled={!id}><Trash2 className="h-4 w-4 text-red-600" /></Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span>Page {page} of {Math.max(totalPages, 1)}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
      <CustomerCreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={reload} />
    </Section>
  );
};

// ───────────────────────────────────────────────────────────
// Products section (list + create + categories inline)
// ───────────────────────────────────────────────────────────
const ProductCreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', sku: '', category_id: '', type: 'product', cost: '', sales_price: '' });
  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.category_id) { toast.error('Category ID is required'); return; }
    if (!form.sales_price || parseFloat(form.sales_price) < 0) { toast.error('Sales price is required'); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), description: form.description?.trim() || undefined, sku: form.sku?.trim() || undefined,
        category_id: parseInt(form.category_id, 10), type: form.type,
        cost: form.cost ? parseFloat(form.cost) : undefined, sales_price: parseFloat(form.sales_price),
      };
      const res = await api.post('/payment-center/products', body);
      if (res.data?.ok) { toast.success('Product created'); onCreated?.(); onOpenChange(false); setForm({ name: '', description: '', sku: '', category_id: '', type: 'product', cost: '', sales_price: '' }); }
      else toast.error(res.data?.error_message || `Provider rejected (${res.data?.error_code || 'error'})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Request failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payment-center-prod-create-dialog">
        <DialogHeader><DialogTitle>New Product / Service</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="prod-name" /></div>
          <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU</Label><Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} /></div>
            <div><Label>Category ID *</Label><Input type="number" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Type</Label>
              <select className="w-full border rounded-md h-9 px-3 text-sm" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="product">Product</option><option value="service">Service</option>
              </select>
            </div>
            <div><Label>Cost</Label><Input type="number" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></div>
            <div><Label>Sales price *</Label><Input type="number" step="0.01" value={form.sales_price} onChange={e => setForm({ ...form, sales_price: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="prod-submit-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProductsSection = () => {
  const { envelope, loading, page, setPage, keywords, setKeywords, reload } = useListEndpoint('/payment-center/products');
  const [createOpen, setCreateOpen] = useState(false);
  const rows = envelope?.ok ? (envelope.data?.product_services || envelope.data?.products || envelope.data?.data || []) : [];
  const totalPages = envelope?.ok ? parseInt(envelope.data?.page_count || 1, 10) : 1;
  return (
    <Section
      title="Products & Services"
      description="Catalog items available to attach to payment requests."
      testId="payment-center-products-section"
      loading={loading} envelope={envelope}
      isEmpty={envelope?.ok && rows.length === 0}
      emptyMessage="No products or services found."
      actions={
        <>
          <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" /><Input className="pl-8 h-9 w-56" placeholder="Search catalog" value={keywords} onChange={e => setKeywords(e.target.value)} /></div>
          <Button size="sm" variant="outline" onClick={reload} disabled={loading}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="prod-new-btn"><Plus className="h-4 w-4 mr-1.5" />New item</Button>
        </>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">SKU</th>
            <th className="text-left px-3 py-2">Category</th><th className="text-right px-3 py-2">Cost</th>
            <th className="text-right px-3 py-2">Sales price</th>
          </tr></thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.id || i} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{p.id || '—'}</td>
                <td className="px-3 py-2 font-medium">{p.name || '—'}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] capitalize">{p.type || '—'}</Badge></td>
                <td className="px-3 py-2 font-mono text-xs">{p.sku || '—'}</td>
                <td className="px-3 py-2">{p.category || '—'}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(p.cost)}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtMoney(p.sales_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span>Page {page} of {Math.max(totalPages, 1)}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
      <ProductCreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={reload} />
    </Section>
  );
};

// ───────────────────────────────────────────────────────────
// v3.18-1345 A3 — POS Links section HIDDEN.
// The provider docs for POS Link create / update / get / generate URLs
// are not yet available. Per the operational-cleanup rule ("do not
// expose unfinished functionality to operators"), the tab is removed
// from the Payment Center TABS array below. The section component is
// kept intentionally so it can be re-enabled by adding it back to
// the TABS list once URLs are supplied — no code deletion required.
// ───────────────────────────────────────────────────────────
const PosLinksSection = () => (
  <div data-testid="payment-center-pos-links-section">
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-slate-900">POS Links</h2>
      <p className="text-xs text-slate-500 mt-0.5">Point-of-sale link management.</p>
    </div>
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-6 py-8 text-sm text-amber-900">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" />
        <div>
          <p className="font-medium">Pending upstream documentation</p>
          <p className="mt-1 text-xs text-amber-900/80">
            The request URLs for POS Link create / update / get / generate are not present in the
            documentation snapshot returned from the provider docs. Per the Phase 2 rule
            (&quot;do not invent endpoints&quot;), this section is intentionally not wired up. It will be enabled once
            the documented URLs are supplied.
          </p>
        </div>
      </div>
    </div>
  </div>
);


// ───────────────────────────────────────────────────────────
// Webhooks section (audit-trail view, non-caching)
// ───────────────────────────────────────────────────────────

/**
 * Row rendering one environment's Webhook URL.
 *
 * The full token is NEVER rendered. On "Copy Webhook URL" click, the
 * frontend fetches `/webhook-config/reveal-url` (auth-gated), writes
 * the returned URL directly to the clipboard, discards it from memory,
 * and shows a transient "Copied" confirmation. The unmasked URL never
 * enters React state that is rendered by the DOM.
 */
const UrlRow = ({ label, testId, masked, environment, hasHost, tokenConfigured, revealPath = '/payment-center/webhook-config/reveal-url' }) => {
  const [copying, setCopying] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState(false);

  const canCopy = hasHost && tokenConfigured;

  const copy = async () => {
    if (!canCopy || copying) return;
    setCopying(true);
    try {
      const res = await api.get(revealPath, { params: { environment } });
      const url = res?.data?.url;
      if (!url) throw new Error('empty url');
      try {
        await navigator.clipboard.writeText(url);
      } catch (clipboardErr) {
        // Fallback for browsers/contexts without Clipboard API access.
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
      }
      toast.success(`${label} copied to clipboard`);
      setCopiedFlash(true);
      setTimeout(() => setCopiedFlash(false), 2000);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Copy failed';
      toast.error(typeof msg === 'string' ? msg : 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <span className="text-slate-500 text-xs w-28 pt-2">{label}</span>
      <div className="flex-1 min-w-0">
        <code
          className="block font-mono text-[11px] bg-slate-100 px-2 py-1.5 rounded break-all"
          data-testid={`${testId}-masked`}
        >
          {masked || <span className="text-slate-400 italic">— host not configured —</span>}
        </code>
      </div>
      <Button
        size="sm"
        variant={copiedFlash ? 'default' : 'outline'}
        onClick={copy}
        disabled={!canCopy || copying}
        className="h-8 text-xs whitespace-nowrap"
        data-testid={`${testId}-copy-btn`}
      >
        {copying ? (
          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
        ) : copiedFlash ? (
          <ShieldCheck className="h-3.5 w-3.5 mr-1" />
        ) : (
          <Copy className="h-3.5 w-3.5 mr-1" />
        )}
        {copiedFlash ? 'Copied' : 'Copy Webhook URL'}
      </Button>
    </div>
  );
};

const EmsTokenMissingCallout = ({ onGenerated }) => {
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    if (!window.confirm('Generate a new EMS webhook token? This value will be written to backend/.env and become active on the next request.')) return;
    setBusy(true);
    try {
      const res = await api.post('/payment-center/webhook-config/generate-token-ems');
      if (res?.data?.persisted_to_env_file === false) {
        toast.warning(`Token generated but could not be persisted to .env: ${res.data.persist_error || 'unknown error'}. Set PAYMENT_PROVIDER_T2_WEBHOOK_TOKEN manually.`);
      } else {
        toast.success('EMS webhook token generated.');
      }
      onGenerated?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to generate token';
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate token');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
      data-testid="wh-ems-token-missing-callout"
    >
      <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-xs text-amber-900">
        <p className="font-semibold">EMS Webhook Token not configured</p>
        <p className="mt-0.5 leading-snug">
          The EMS Merchant Control Panel cannot deliver callbacks until a shared secret is in place.
          Generate one now — it will be written to backend/.env and used immediately.
        </p>
      </div>
      <Button
        size="sm"
        onClick={generate}
        disabled={busy}
        className="bg-amber-700 hover:bg-amber-800 text-white h-8 text-xs whitespace-nowrap"
        data-testid="wh-ems-generate-token-btn"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
        Generate Token
      </Button>
    </div>
  );
};

// v3.18-1345 A2 — Primary (Kurv) webhook token rotation button.
// Mints a new PAYMENT_PROVIDER_WEBHOOK_TOKEN server-side, invalidates
// the previous token, persists to .env, and writes an audit row.
const KurvRegenerateTokenButton = ({ onRotated }) => {
  const [busy, setBusy] = useState(false);
  const rotate = async () => {
    const ok = window.confirm(
      'Rotate the Kurv webhook token?\n\n'
      + 'The previous token will be immediately invalidated. Any inbound '
      + 'webhook using the old token will start returning 401 as soon as this '
      + 'action completes.\n\n'
      + 'After rotation, click "Copy Webhook URL" to get the new URL and paste '
      + 'it into the Kurv Developer Portal.\n\n'
      + 'Continue?'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.post('/payment-center/webhook-config/rotate-token');
      if (res?.data?.persisted_to_env_file === false) {
        toast.warning(
          `Token rotated in memory but not persisted to .env: ${res.data.persist_error || 'unknown error'}. Restart may lose the change.`
        );
      } else {
        toast.success(`Kurv webhook token rotated. New token: ${res?.data?.new_token_masked || 'new'}. Copy the new URL now.`);
      }
      onRotated?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to rotate token';
      toast.error(typeof msg === 'string' ? msg : 'Failed to rotate token');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={rotate}
      disabled={busy}
      className="h-7 text-[11px] ml-1"
      data-testid="wh-regenerate-token-btn"
      title="Rotate PAYMENT_PROVIDER_WEBHOOK_TOKEN — invalidates the previous token immediately"
    >
      {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
      Regenerate Token
    </Button>
  );
};


const WebhooksSection = () => {
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [cfg, ev] = await Promise.all([
        api.get('/payment-center/webhook-config'),
        api.get('/payment-center/webhook-events', { params: { limit: 50 } }),
      ]);
      setConfig(cfg.data);
      setEvents(ev.data?.events || []);
    } catch (e) {
      const m = e?.response?.data?.detail || 'Failed to load webhook data';
      setError(typeof m === 'string' ? m : 'Failed to load webhook data');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="payment-center-webhooks-section">
      <div className="flex items-start justify-between gap-6 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Webhooks</h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Inbound Payment Response callbacks from the provider. Recent events are read live
            from the immutable audit collection — no snapshot storage, no cache.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="wh-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Refresh
        </Button>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="wh-error">{error}</div>
      ) : null}

      {config ? (
        <Card className="mb-5 border-slate-200" data-testid="wh-config-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-md bg-slate-900 text-white"><KeyRound className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">Receiver configuration</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Configure this URL in the provider Developer Portal &ldquo;Webhook URL&rdquo; field. The token
                  authenticates every inbound request and is never logged.
                </p>

                {/* Environment URL rows */}
                <div className="mt-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Terminal 1 (Kurv)</p>
                  <UrlRow
                    label="Production URL"
                    testId="wh-url-production"
                    masked={config.production_url_masked}
                    environment="production"
                    hasHost={!!config.production_host}
                    tokenConfigured={!!config.token_configured}
                  />
                  <UrlRow
                    label="Preview URL"
                    testId="wh-url-preview"
                    masked={config.preview_url_masked}
                    environment="preview"
                    hasHost={!!config.preview_host}
                    tokenConfigured={!!config.token_configured}
                  />
                  <div className="border-t border-slate-100 pt-3">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">Terminal 2 (EMS)</p>
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${config.ems_token_configured ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`}
                        data-testid="wh-ems-token-status"
                      >
                        {config.ems_token_configured ? 'Token Configured' : 'Token Missing'}
                      </Badge>
                    </div>
                    {config.ems_token_configured ? (
                      <div className="space-y-3">
                        <UrlRow
                          label="Production URL"
                          testId="wh-ems-url-production"
                          masked={config.ems_production_url_masked}
                          environment="production"
                          hasHost={!!config.production_host}
                          tokenConfigured={!!config.ems_token_configured}
                          revealPath="/payment-center/webhook-config/reveal-url-ems"
                        />
                        <UrlRow
                          label="Preview URL"
                          testId="wh-ems-url-preview"
                          masked={config.ems_preview_url_masked}
                          environment="preview"
                          hasHost={!!config.preview_host}
                          tokenConfigured={!!config.ems_token_configured}
                          revealPath="/payment-center/webhook-config/reveal-url-ems"
                        />
                      </div>
                    ) : (
                      <EmsTokenMissingCallout onGenerated={load} />
                    )}
                  </div>
                </div>

                {/* Static metadata rows */}
                <div className="mt-4 grid grid-cols-1 gap-2 text-xs border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-28">Path</span>
                    <code className="font-mono bg-slate-100 px-2 py-0.5 rounded">{config.receiver_path}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-28">Query param</span>
                    <code className="font-mono bg-slate-100 px-2 py-0.5 rounded">?{config.receiver_query_param}=&lt;TOKEN&gt;</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-28">Token</span>
                    <code className="font-mono bg-slate-100 px-2 py-0.5 rounded" data-testid="wh-config-token-masked">
                      {config.token_masked || '— not configured —'}
                    </code>
                    <Badge variant="outline" className={`text-[10px] ${config.token_configured ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`}>
                      {config.token_configured ? 'Configured' : 'Missing'}
                    </Badge>
                    <KurvRegenerateTokenButton onRotated={load} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-28">Idempotency</span>
                    <span className="text-slate-700">{config.idempotency_key}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 w-28">Signature</span>
                    <span className="text-slate-500 italic">Provider docs specify none — Hub-owned URL secret is the auth model.</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {loading && events.length === 0 ? (
        <div className="flex items-center gap-2 text-slate-500 py-8"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Loading events…</span></div>
      ) : events.length === 0 ? (
        <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500" data-testid="wh-empty">
          No webhook events recorded yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="wh-events-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
              <th className="text-left px-3 py-2">Received</th>
              <th className="text-left px-3 py-2">Payment ID</th>
              <th className="text-left px-3 py-2">Transaction</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Result</th>
              <th className="text-right px-3 py-2">Latency</th>
              <th className="text-left px-3 py-2">Source</th>
            </tr></thead>
            <tbody>
              {events.map(e => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`wh-row-${e.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtTime(e.timestamp)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.payment_id || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.transaction_id || '—'}</td>
                  <td className="px-3 py-2">{e.reference_number || '—'}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmtMoney(e.amount, e.currency)}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{e.status || '—'}</Badge></td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${e.result === 'duplicate' ? 'border-amber-300 text-amber-700' : 'border-emerald-300 text-emerald-700'}`}>
                      {e.result || '—'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-slate-500">{e.processing_latency_ms != null ? `${e.processing_latency_ms} ms` : '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{e.source_ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────────────────
// Charge Card section (Payment Center → client selector → same dialog)
// ───────────────────────────────────────────────────────────
const ChargeCardSection = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [context, setContext] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [receiptTx, setReceiptTx] = useState(null);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        const res = await api.get('/payments/engine/clients/search', { params: { q } });
        setResults(res.data?.clients || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [query]);

  const loadContext = useCallback(async (client) => {
    setSelectedClient(client);
    setLoadingCtx(true);
    setContext(null);
    try {
      const [ctxRes, txRes] = await Promise.all([
        api.get(`/payments/engine/clients/${client.id}/context`),
        api.get(`/payments/engine/clients/${client.id}/transactions`, { params: { limit: 25 } }),
      ]);
      setContext(ctxRes.data);
      setTransactions(txRes.data?.transactions || []);
      setQuery('');
      setResults([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load client context');
    } finally {
      setLoadingCtx(false);
    }
  }, []);

  const refreshAfterCharge = async () => {
    if (!selectedClient) return;
    try {
      const [ctxRes, txRes] = await Promise.all([
        api.get(`/payments/engine/clients/${selectedClient.id}/context`),
        api.get(`/payments/engine/clients/${selectedClient.id}/transactions`, { params: { limit: 25 } }),
      ]);
      setContext(ctxRes.data);
      setTransactions(txRes.data?.transactions || []);
    } catch { /* refresh best-effort; errors surface elsewhere */ }
  };

  return (
    <div data-testid="payment-center-charge-card-section">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Charge Card</h2>
        <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
          Search a client. The Hub loads the active deal, purchase proposal, outstanding balance,
          previous payments, and Money Manager linkage automatically. One canonical engine backs
          every payment initiated from anywhere in the Hub.
        </p>
      </div>

      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
        <Input
          className="pl-9 h-11"
          placeholder="Search clients by name, email, phone, or company"
          value={query}
          onChange={e => setQuery(e.target.value)}
          data-testid="charge-card-client-search"
        />
        {(query.trim() && (results.length > 0 || searching)) ? (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-64 overflow-y-auto" data-testid="charge-card-client-results">
            {searching ? (
              <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />Searching…
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500">No matches.</div>
            ) : (
              results.map(c => (
                <button
                  key={c.id}
                  onClick={() => loadContext(c)}
                  className="block w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0"
                  data-testid={`charge-card-client-option-${c.id}`}
                >
                  <div className="font-medium">{c.full_name || '—'}</div>
                  <div className="text-xs text-slate-500">
                    {[c.email, c.phone, c.company_name].filter(Boolean).join(' • ') || '—'}
                  </div>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      {loadingCtx ? (
        <div className="flex items-center gap-2 text-slate-500 py-8">
          <Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Loading client context…</span>
        </div>
      ) : !selectedClient ? (
        <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
          Select a client to begin.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="charge-card-context">
          {/* Client card */}
          <Card className="border-slate-200 lg:col-span-1">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-slate-900 text-white"><User className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 truncate">{context?.client?.full_name || '—'}</p>
                  <p className="text-xs text-slate-500 truncate">{context?.client?.email || '—'}</p>
                  <p className="text-xs text-slate-500 truncate">{context?.client?.phone || '—'}</p>
                  {context?.client?.company_name ? (
                    <Badge variant="outline" className="mt-2 text-[10px]">{context.client.company_name}</Badge>
                  ) : null}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-y-2 text-xs">
                <div className="text-slate-500">Outstanding</div>
                <div className="text-slate-900 font-semibold" data-testid="charge-card-summary-outstanding">
                  {fmtMoney(context?.outstanding_balance)}
                </div>
                <div className="text-slate-500">Collected</div>
                <div className="text-slate-700">{fmtMoney(context?.total_collected)}</div>
                <div className="text-slate-500">Active deals</div>
                <div className="text-slate-700">{(context?.active_deals || []).length}</div>
                <div className="text-slate-500">MM entry</div>
                <div className="text-slate-700">{context?.money_manager_entry ? 'Linked' : '—'}</div>
                <div className="text-slate-500">Proposal</div>
                <div className="text-slate-700">{context?.purchase_proposal ? 'Linked' : '—'}</div>
              </div>
              <div className="mt-4">
                <Button
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                  onClick={() => setChargeOpen(true)}
                  data-testid="charge-card-open-btn"
                >
                  <Zap className="h-3.5 w-3.5 mr-1.5" />Charge Card
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Finance history (previous payments) */}
          <Card className="border-slate-200 lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-slate-900">Finance History</p>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">last 25</span>
              </div>
              {transactions.length === 0 ? (
                <div className="text-xs text-slate-500 italic">No payment transactions yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 uppercase text-[10px]">
                      <tr>
                        <th className="text-left py-1">Date</th>
                        <th className="text-left py-1">Hub ref</th>
                        <th className="text-right py-1">Amount</th>
                        <th className="text-left py-1">Terminal</th>
                        <th className="text-left py-1">Status</th>
                        <th className="text-left py-1">Auth #</th>
                        <th className="text-left py-1">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(tx => (
                        <tr key={tx.id} className="border-t border-slate-100" data-testid={`charge-card-tx-row-${tx.id}`}>
                          <td className="py-1 text-slate-500">{fmtTime(tx.created_at)}</td>
                          <td className="py-1 font-mono">{tx.hub_reference}</td>
                          <td className="py-1 text-right font-medium">{fmtMoney(tx.amount, tx.currency)}</td>
                          <td className="py-1">{tx.terminal_label}</td>
                          <td className="py-1">
                            <Badge variant="outline" className={`text-[10px] ${
                              tx.status === 'approved' ? 'border-emerald-300 text-emerald-700' :
                              tx.status === 'declined' ? 'border-red-300 text-red-700' :
                              tx.status === 'error' ? 'border-red-300 text-red-700' :
                              'border-amber-300 text-amber-700'
                            }`}>{tx.status || '—'}</Badge>
                          </td>
                          <td className="py-1 font-mono">{tx.provider_authorization_number || '—'}</td>
                          <td className="py-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[11px] px-2"
                              onClick={() => setReceiptTx(tx)}
                              disabled={tx.status === 'error'}
                              data-testid={`charge-card-view-receipt-${tx.id}`}
                            >
                              View Receipt
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <ChargeCardDialog
        open={chargeOpen}
        onOpenChange={setChargeOpen}
        clientId={selectedClient?.id}
        dealId={context?.auto_selected_deal_id || null}
        defaultAmount={context?.outstanding_balance ? context.outstanding_balance.toFixed(2) : undefined}
        onCharged={refreshAfterCharge}
      />
      <ReceiptViewer
        open={!!receiptTx}
        onOpenChange={(v) => { if (!v) setReceiptTx(null); }}
        transaction={receiptTx}
      />
    </div>
  );
};

// ───────────────────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',         label: 'Overview',           icon: LayoutGrid, Component: OverviewSection },
  { key: 'charge-card',      label: 'Charge Card',        icon: Zap,        Component: ChargeCardSection },
  { key: 'payment-requests', label: 'Payment Requests',   icon: FileText,   Component: PaymentRequestsSection },
  { key: 'payment-history',  label: 'Payment History',    icon: ScrollText, Component: PaymentHistorySection },
  { key: 'customers',        label: 'Customers',          icon: Users,      Component: CustomersSection },
  { key: 'products',         label: 'Products & Services', icon: Package,   Component: ProductsSection },
  { key: 'webhooks',         label: 'Webhooks',           icon: Webhook,    Component: WebhooksSection },
  // v3.18-1345 A3 — POS Links intentionally omitted. See PosLinksSection
  // definition above (kept for future re-enable) and the top-level
  // hide comment. Re-add this entry once provider URL docs are supplied.
];

const PaymentCenterPage = () => {
  const [active, setActive] = useState('overview');
  const Active = useMemo(() => TABS.find(t => t.key === active)?.Component || OverviewSection, [active]);
  return (
    <div className="p-6 lg:p-8" data-testid="payment-center-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Payment Center</h1>
        <p className="mt-1 text-sm text-slate-500 max-w-2xl">
          Live provider data. No local caching, no scheduled polling, no synced snapshots.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6 border-b border-slate-200 pb-3" role="tablist" data-testid="payment-center-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const activeCls = active === t.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200';
          return (
            <button key={t.key} onClick={() => setActive(t.key)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeCls}`} data-testid={`payment-center-tab-${t.key}`}>
              <Icon className="h-3.5 w-3.5" />{t.label}
            </button>
          );
        })}
      </div>
      <div className="max-w-6xl"><Active /></div>
    </div>
  );
};

export default PaymentCenterPage;

